import {Component, Input, ViewChild, ElementRef, OnInit, OnDestroy, ChangeDetectorRef} from '@angular/core';
import {AuthService} from '../shared/services/auth.service';
import {ActivatedRoute, Router} from '@angular/router';
import {isDefined} from '../shared/utils/utils';
import {environment} from '../../environments/environment';
import {WebmateOptions} from '@webmate-web-core/webmate-options';
import {merge, Observable, Subscription} from 'rxjs';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {filter, mergeMap, take, tap} from 'rxjs/operators';
import {FeatureService} from '@webmate-web-core/shared/services/feature.service';
import {UserService} from '@webmate-web-core/shared/services/user.service';
import {encodeReturnUrl, extractReturnUrl} from '@webmate-web-core/shared/utils/route-access';
import {User} from '@webmate-web-core/shared/types/user/user';
import {Feature} from '@webmate-web-core/shared/types/feature';


@UntilDestroy()
@Component({
    selector: 'wm-login',
    styleUrls: ['./login.component.less'],
    templateUrl: './login.component.html'
})
export class LoginComponent implements OnInit, OnDestroy {

    demoInstance: boolean;

    email: string = '';
    password: string = '';

    // auth feature to check for sso
    authFeature?: Feature;

    // original location where the user wanted to navigate to
    returnUrl: string;
    loggingIn: boolean = false;
    loginFailure: boolean = false;

    authSubscription?: Subscription;

    constructor(private authService: AuthService, public webmateOptions: WebmateOptions, private route: ActivatedRoute, private router: Router,
                private featureService: FeatureService, private userService: UserService) {
        this.demoInstance = webmateOptions.demoenv;
    }

    checkForm() {
        return this.email.trim() === '' || this.password.trim() === '';
    }

    ngOnInit() {
        this.route.queryParams.pipe(untilDestroyed(this)).subscribe(p => {
            this.returnUrl = extractReturnUrl(p);
        });
        const source$ = this.authService.observeAuthStatus();
        this.authSubscription = this.handleAuthSuccess(source$);
    }

    ngOnDestroy() {
        this.authSubscription?.unsubscribe();
    }

    submitLogin() {
        if (this.checkForm()) return;
        this.loggingIn = true;
        this.authSubscription?.unsubscribe();

        const source$ = this.authService.authenticateUserWithPasswordGrant(this.email, this.password).pipe(
            tap(authSuccess => {
                if (authSuccess) {
                    this.email = '';
                } else {
                    this.loggingIn = false;
                    this.loginFailure = true;
                }
                this.password = '';
            }));
        this.handleAuthSuccess(source$);
    }

    private handleAuthSuccess(source$: Observable<boolean>): Subscription {
        return source$.pipe(filter(authSuccess => authSuccess), mergeMap(() => {
            return this.featureService.getFeature('webmate.auth');
        }), mergeMap(feature => {
            this.authFeature = feature;
            return this.userService.getCurrentUserData();
        }), take(1)).subscribe((user) => {
            this.navigateAfterLogin(user);
        });
    }

    private navigateAfterLogin(user: User) {
        if (!!this.authFeature && this.authFeature.isActive && this.authFeature.migrateExistingUsers && !user.hasSSOUserId) {
            // the customer forces users to use SSO. Redirect them to the first time setup
            this.router.navigate(['sso'], {relativeTo: this.route, queryParams: {migrateUser: true, returnUrl: encodeReturnUrl(this.returnUrl)}});
        } else {
            this.router.navigateByUrl(this.returnUrl).catch(err => {
                this.router.navigate(['/']);
            }).then((success) => {
                if (success === false) {
                    this.router.navigate(['/']);
                }
            });
        }
    }
}
